
import { Search, Filter, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const EVENT_TYPES = [
  { value: "conference", label: "Conference" },
  { value: "trade_show", label: "Trade Show" },
  { value: "webinar", label: "Webinar" },
  { value: "workshop", label: "Workshop" },
  { value: "networking", label: "Networking" },
];

const STATUSES = [
  { value: "upcoming", label: "Upcoming" },
  { value: "attending", label: "Attending" },
  { value: "attended", label: "Attended" },
  { value: "cancelled", label: "Cancelled" },
  { value: "watching", label: "Watching" },
];

const TIERS = [
  { value: "Tier 1: Must-Attend", label: "Must-Attend" },
  { value: "Tier 2: High-Value", label: "High-Value" },
  { value: "Tier 3: Monitor", label: "Monitor" },
];

const CATEGORIES = [
  { value: "Bioprocessing & Manufacturing", label: "Bioprocessing & Manufacturing" },
  { value: "Cell Therapy Scientific", label: "Cell Therapy Scientific" },
  { value: "Gene Therapy Scientific", label: "Gene Therapy Scientific" },
  { value: "CGT Scientific Conference", label: "CGT Scientific Conference" },
  { value: "Equipment & Trade Show", label: "Equipment & Trade Show" },
  { value: "Investor & Partnering", label: "Investor & Partnering" },
  { value: "Regional Networking", label: "Regional Networking" },
  { value: "Regulatory & Clinical", label: "Regulatory & Clinical" },
  { value: "General Biotech", label: "General Biotech" },
];

const TERRITORIES = [
  { value: "Massachusetts", label: "Andrew (Massachusetts)" },
  { value: "West", label: "Bethanne (West)" },
  { value: "South / Central", label: "Jeff (South / Central)" },
  { value: "Northeast / Midwest", label: "Nico (Northeast / Midwest)" },
  { value: "EMEA", label: "Paul (EMEA)" },
  { value: "APAC", label: "Kelvin (APAC)" },
];

export interface EventFilters {
  search: string;
  eventType: string[];
  status: string[];
  tier: string[];
  category: string[];
  territory: string[];
}

interface EventFilterSidebarProps {
  filters: EventFilters;
  onFiltersChange: (filters: EventFilters) => void;
  totalCount: number;
}

export function EventFilterSidebar({ filters, onFiltersChange, totalCount }: EventFilterSidebarProps) {
  const hasActiveFilters = filters.search || filters.eventType.length > 0 || filters.status.length > 0 ||
    filters.tier.length > 0 || filters.category.length > 0 || filters.territory.length > 0;

  const toggleArray = (arr: string[], value: string) =>
    arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value];

  return (
    <div className="space-y-4 p-4 bg-card rounded-lg border">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-muted-foreground" />
          <span className="font-medium">Filters</span>
          <Badge variant="secondary">{totalCount} events</Badge>
        </div>
        {hasActiveFilters && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onFiltersChange({ search: "", eventType: [], status: [], tier: [], category: [], territory: [] })}
            className="h-8"
          >
            <X className="h-4 w-4 mr-1" />
            Clear All
          </Button>
        )}
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search events..."
          value={filters.search}
          onChange={(e) => onFiltersChange({ ...filters, search: e.target.value })}
          className="pl-9"
        />
      </div>

      <Accordion type="multiple" defaultValue={["tier", "category"]} className="space-y-2">
        <AccordionItem value="tier" className="border rounded-lg px-3">
          <AccordionTrigger className="text-sm font-medium py-3">CARR Tier</AccordionTrigger>
          <AccordionContent className="pb-4">
            <div className="space-y-2">
              {TIERS.map((t) => (
                <div key={t.value} className="flex items-center space-x-2">
                  <Checkbox
                    id={`tier-${t.value}`}
                    checked={filters.tier.includes(t.value)}
                    onCheckedChange={() =>
                      onFiltersChange({ ...filters, tier: toggleArray(filters.tier, t.value) })
                    }
                  />
                  <label htmlFor={`tier-${t.value}`} className="text-sm leading-none">
                    {t.label}
                  </label>
                </div>
              ))}
            </div>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="category" className="border rounded-lg px-3">
          <AccordionTrigger className="text-sm font-medium py-3">Category</AccordionTrigger>
          <AccordionContent className="pb-4">
            <div className="space-y-2">
              {CATEGORIES.map((c) => (
                <div key={c.value} className="flex items-center space-x-2">
                  <Checkbox
                    id={`cat-${c.value}`}
                    checked={filters.category.includes(c.value)}
                    onCheckedChange={() =>
                      onFiltersChange({ ...filters, category: toggleArray(filters.category, c.value) })
                    }
                  />
                  <label htmlFor={`cat-${c.value}`} className="text-sm leading-none">
                    {c.label}
                  </label>
                </div>
              ))}
            </div>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="territory" className="border rounded-lg px-3">
          <AccordionTrigger className="text-sm font-medium py-3">Territory / Rep</AccordionTrigger>
          <AccordionContent className="pb-4">
            <div className="space-y-2">
              {TERRITORIES.map((t) => (
                <div key={t.value} className="flex items-center space-x-2">
                  <Checkbox
                    id={`terr-${t.value}`}
                    checked={filters.territory.includes(t.value)}
                    onCheckedChange={() =>
                      onFiltersChange({ ...filters, territory: toggleArray(filters.territory, t.value) })
                    }
                  />
                  <label htmlFor={`terr-${t.value}`} className="text-sm leading-none">
                    {t.label}
                  </label>
                </div>
              ))}
            </div>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="type" className="border rounded-lg px-3">
          <AccordionTrigger className="text-sm font-medium py-3">Event Type</AccordionTrigger>
          <AccordionContent className="pb-4">
            <div className="space-y-2">
              {EVENT_TYPES.map((t) => (
                <div key={t.value} className="flex items-center space-x-2">
                  <Checkbox
                    id={`etype-${t.value}`}
                    checked={filters.eventType.includes(t.value)}
                    onCheckedChange={() =>
                      onFiltersChange({ ...filters, eventType: toggleArray(filters.eventType, t.value) })
                    }
                  />
                  <label htmlFor={`etype-${t.value}`} className="text-sm leading-none">
                    {t.label}
                  </label>
                </div>
              ))}
            </div>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="status" className="border rounded-lg px-3">
          <AccordionTrigger className="text-sm font-medium py-3">Status</AccordionTrigger>
          <AccordionContent className="pb-4">
            <div className="space-y-2">
              {STATUSES.map((s) => (
                <div key={s.value} className="flex items-center space-x-2">
                  <Checkbox
                    id={`estatus-${s.value}`}
                    checked={filters.status.includes(s.value)}
                    onCheckedChange={() =>
                      onFiltersChange({ ...filters, status: toggleArray(filters.status, s.value) })
                    }
                  />
                  <label htmlFor={`estatus-${s.value}`} className="text-sm leading-none">
                    {s.label}
                  </label>
                </div>
              ))}
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
}
